'use client';
import React from 'react';
import { AfterHoursAnalysis } from '@/lib/services/forgeBehaviorService';

interface AfterHoursAnalysisCardProps {
  analysis: AfterHoursAnalysis;
}

export default function AfterHoursAnalysisCard({ analysis }: AfterHoursAnalysisCardProps) {
  const increase = analysis.lateNightCalorieIncrease;
  const hasData = analysis.cannabisDaysCount >= 2;

  return (
    <div className="card-forge p-5 space-y-4">
      <div>
        <h3 className="text-forge-heading font-bold tracking-wider text-sm uppercase">After Hours Analysis</h3>
        <p className="text-2xs text-muted-foreground mt-0.5">Calories consumed before vs after cannabis use</p>
      </div>

      {!hasData ? (
        <div className="py-6 text-center">
          <p className="text-muted-foreground text-sm">Log at least 2 cannabis days with calorie data to see after-hours analysis.</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-card-elevated rounded-lg p-4 text-center">
              <div className="text-2xs text-muted-foreground uppercase tracking-wider mb-1">Avg Cals After Cannabis</div>
              <div className="text-2xl font-black text-foreground">{Math.round(analysis.avgCaloriesAfterCannabis)}</div>
              <div className="text-2xs text-muted-foreground">kcal</div>
            </div>
            <div className="bg-card-elevated rounded-lg p-4 text-center">
              <div className="text-2xs text-muted-foreground uppercase tracking-wider mb-1">Avg Cals Without</div>
              <div className="text-2xl font-black text-foreground">{Math.round(analysis.avgCaloriesWithoutCannabis)}</div>
              <div className="text-2xs text-muted-foreground">kcal</div>
            </div>
          </div>

          <div className={`rounded-lg p-4 flex items-center justify-between ${
            Math.abs(increase) < 50
              ? 'bg-muted/10 border border-border'
              : increase > 0
              ? 'bg-amber-500/10 border border-amber-500/20' :'bg-green-500/10 border border-green-500/20'
          }`}>
            <div>
              <div className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Late-Night Calorie Shift</div>
              <div className={`text-2xl font-black mt-1 ${
                Math.abs(increase) < 50 ? 'text-muted-foreground' : increase > 0 ? 'text-amber-400' : 'text-green-400'
              }`}>
                {increase > 0 ? '+' : ''}{Math.round(increase)} kcal
              </div>
            </div>
            <div className="text-right">
              <div className="text-xs text-muted-foreground">Unplanned avg</div>
              <div className="text-lg font-bold text-foreground">{Math.round(analysis.avgUnplannedCalories)}</div>
              <div className="text-2xs text-muted-foreground">kcal</div>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">Avg Protein After Cannabis</span>
              <span className="font-bold text-foreground">{Math.round(analysis.avgProteinAfterCannabis)}g</span>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">Avg Protein Without Cannabis</span>
              <span className="font-bold text-foreground">{Math.round(analysis.avgProteinWithoutCannabis)}g</span>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">Cannabis days tracked</span>
              <span className="font-bold text-foreground">{analysis.cannabisDaysCount}</span>
            </div>
          </div>

          <p className="text-2xs text-muted-foreground/50 italic">
            Correlation does not necessarily equal causation. These are observations only.
          </p>
        </>
      )}
    </div>
  );
}
